import { useState, useEffect, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CalendarIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import { 
  getPayments, 
  getPaymentAnalytics,
  deletePayment,
  processPayment,
  completePayment,
  failPayment,
  type Payment,
  type PaymentFilters,
  type PaymentAnalytics,
  PaymentType,
  PaymentMethod,
  PaymentStatus,
  PaymentCategory,
  Currency,
  GatewayProvider,
  getPaymentTypeDisplay,
  getPaymentMethodDisplay,
  getPaymentStatusDisplay,
  getPaymentCategoryDisplay,
  getCurrencyDisplay,
  getGatewayProviderDisplay,
  createPayment,
  type CreatePaymentDto,
  RecurringFrequency,
  updatePayment,
  type UpdatePaymentDto,
} from '../api/payments';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import SortOrderButton from '../components/ui/SortOrderButton';
import AmountInput from '../components/ui/AmountInput';

export default function Payments() {
  // Data state
  const [payments, setPayments] = useState<Payment[]>([]);
  const [analytics, setAnalytics] = useState<PaymentAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<PaymentFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<string>('paymentDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [formData, setFormData] = useState<Partial<UpdatePaymentDto>>({
    paymentType: PaymentType.INCOMING,
    paymentMethod: PaymentMethod.CASH,
    currency: Currency.ZAR,
    status: PaymentStatus.PENDING,
    category: PaymentCategory.SALES,
    amount: 0,
    description: '',
    paymentDate: new Date().toISOString().split('T')[0],
    isRecurring: false,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load payments and analytics using real API
      const [paymentsData, analyticsData] = await Promise.all([
        getPayments({
          page: currentPage,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
          search: searchTerm || undefined,
          ...filters,
        }),
        getPaymentAnalytics(filters),
      ]);
      
      
      setPayments(paymentsData);
      setAnalytics(analyticsData);
      setError(null);
    } catch (err) {
      setError('Failed to load payment data');
      console.error('Error loading payment data:', err);
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  // Since API handles filtering, sorting, and pagination, use payments directly
  const filteredPayments = payments;
  const paginatedPayments = payments;
  const totalPages = Math.ceil((analytics?.totalPayments ?? filteredPayments.length) / itemsPerPage);

  // Updated statistics from analytics
  const totalIncoming = analytics?.totalIncoming || 0;
  const totalOutgoing = analytics?.totalOutgoing || 0;
  const pendingPayments = analytics?.pendingPayments || 0;
  const failedPayments = analytics?.failedPayments || 0;

  // Event handlers
  const handleDeletePayment = async () => {
    if (!selectedPayment) return;
    
    try {
      await deletePayment(selectedPayment.id);
      setShowDeleteModal(false);
      setSelectedPayment(null);
      toast.success('Payment deleted successfully');
      // Reload data to refresh the list
      await loadData();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment');
    }
  };

  const handleProcessPayment = async (payment: Payment) => {
    try {
      await processPayment(payment.id);
      toast.success('Payment processed successfully');
      await loadData();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Failed to process payment');
    }
  };

  const handleCompletePayment = async (payment: Payment) => {
    try {
      await completePayment(payment.id);
      toast.success('Payment completed successfully');
      await loadData();
    } catch (error) {
      console.error('Error completing payment:', error);
      toast.error('Failed to complete payment');
    }
  };

  const handleFailPayment = async (payment: Payment, reason?: string) => {
    try {
      await failPayment(payment.id, reason);
      toast.success('Payment marked as failed');
      await loadData();
    } catch (error) {
      console.error('Error failing payment:', error);
      toast.error('Failed to mark payment as failed');
    }
  };

  const clearFilters = () => {
    setFilters({});
  };

  const openViewModal = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  };

  const openEditModal = (payment: Payment) => {
    setSelectedPayment(payment);
    setFormData({
      paymentType: payment.paymentType,
      paymentMethod: payment.paymentMethod,
      amount: payment.amount,
      currency: payment.currency,
      status: payment.status,
      category: payment.category,
      description: payment.description,
      paymentDate: payment.paymentDate.split('T')[0],
      customerName: payment.customerName || '',
      customerEmail: payment.customerEmail || '',
      vendorName: payment.vendorName || '',
      vendorEmail: payment.vendorEmail || '',
      reference: payment.reference || '',
      invoiceNumber: payment.invoiceNumber || '',
      gatewayProvider: payment.gatewayProvider,
      transactionId: payment.transactionId || '',
      notes: payment.notes || '',
      isRecurring: payment.isRecurring,
      recurringFrequency: payment.recurringFrequency,
      nextDueDate: payment.nextDueDate ? payment.nextDueDate.split('T')[0] : '',
    });
    setShowEditModal(true);
  };

  const handleUpdatePayment = async () => {
    if (!selectedPayment) return;
    
    try {
      setFormErrors({});
      
      // Basic validation
      const errors: Record<string, string> = {};
      if (!formData.description?.trim()) {
        errors.description = 'Description is required';
      }
      if (!formData.amount || formData.amount <= 0) {
        errors.amount = 'Amount must be greater than 0';
      }
      if (formData.paymentType === PaymentType.INCOMING && !formData.customerName?.trim()) {
        errors.customerName = 'Customer name is required for incoming payments';
      }
      if (formData.paymentType === PaymentType.OUTGOING && !formData.vendorName?.trim()) {
        errors.vendorName = 'Vendor name is required for outgoing payments';
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }


      setIsSubmitting(true);
      const updatedPayment = await updatePayment(selectedPayment.id, formData as UpdatePaymentDto);
      
      
      setShowEditModal(false);
      setSelectedPayment(null);
      setFormData({
        paymentType: PaymentType.INCOMING,
        paymentMethod: PaymentMethod.CASH,
        currency: Currency.ZAR,
        status: PaymentStatus.PENDING,
        category: PaymentCategory.SALES,
        amount: 0,
        description: '',
        paymentDate: new Date().toISOString().split('T')[0],
        isRecurring: false,
      });
      toast.success('Payment updated successfully');
      
      await loadData();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error('Failed to update payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowDeleteModal(true);
  };

  const getStatusBadge = (payment: Payment) => {
    const statusConfig = {
      [PaymentStatus.PENDING]: { color: 'bg-amber-100 text-amber-800', icon: ClockIcon },
      [PaymentStatus.PROCESSING]: { color: 'bg-cyan-100 text-cyan-800', icon: ClockIcon },
      [PaymentStatus.COMPLETED]: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircleIcon },
      [PaymentStatus.FAILED]: { color: 'bg-rose-100 text-rose-800', icon: XCircleIcon },
      [PaymentStatus.CANCELLED]: { color: 'bg-violet-100 text-violet-800', icon: XCircleIcon },
      [PaymentStatus.REFUNDED]: { color: 'bg-orange-100 text-orange-800', icon: ExclamationTriangleIcon },
    };

    const config = statusConfig[payment.status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {getPaymentStatusDisplay(payment.status)}
      </span>
    );
  };

  const getPaymentTypeBadge = (payment: Payment) => {
    const typeConfig = {
      [PaymentType.INCOMING]: { color: 'bg-emerald-100 text-emerald-800', symbol: '+' },
      [PaymentType.OUTGOING]: { color: 'bg-rose-100 text-rose-800', symbol: '-' },
    };

    const config = typeConfig[payment.paymentType];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.symbol} {getPaymentTypeDisplay(payment.paymentType)}
      </span>
    );
  };

  const getCategoryBadge = (payment: Payment) => {
    const categoryConfig = {
      [PaymentCategory.SALES]: { color: 'bg-emerald-100 text-emerald-800' },
      [PaymentCategory.PURCHASE]: { color: 'bg-cyan-100 text-cyan-800' },
      [PaymentCategory.EXPENSE]: { color: 'bg-rose-100 text-rose-800' },
      [PaymentCategory.REFUND]: { color: 'bg-orange-100 text-orange-800' },
      [PaymentCategory.SERVICE]: { color: 'bg-purple-100 text-purple-800' },
      [PaymentCategory.SUBSCRIPTION]: { color: 'bg-cyan-100 text-cyan-800' },
      [PaymentCategory.OTHER]: { color: 'bg-violet-100 text-violet-800' },
    };

    const config = categoryConfig[payment.category];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {getPaymentCategoryDisplay(payment.category)}
      </span>
    );
  };

  const handleCreatePayment = async () => {
    try {
      setFormErrors({});
      
      // Basic validation
      const errors: Record<string, string> = {};
      if (!formData.description?.trim()) {
        errors.description = 'Description is required';
      }
      if (!formData.amount || formData.amount <= 0) {
        errors.amount = 'Amount must be greater than 0';
      }
      if (!formData.paymentDate) {
        errors.paymentDate = 'Payment date is required';
      }
      if (formData.paymentType === PaymentType.INCOMING && !formData.customerName?.trim()) {
        errors.customerName = 'Customer name is required for incoming payments';
      }
      if (formData.paymentType === PaymentType.OUTGOING && !formData.vendorName?.trim()) {
        errors.vendorName = 'Vendor name is required for outgoing payments';
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      setIsSubmitting(true);
      await createPayment(formData as CreatePaymentDto);
      setShowAddModal(false);
      setFormData({
        paymentType: PaymentType.INCOMING,
        paymentMethod: PaymentMethod.CASH,
        currency: Currency.ZAR,
        status: PaymentStatus.PENDING,
        category: PaymentCategory.SALES,
        amount: 0,
        description: '',
        paymentDate: new Date().toISOString().split('T')[0],
        isRecurring: false,
      });
      toast.success('Payment created successfully');
      await loadData();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Failed to create payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-semibold text-slate-900">Error</h3>
          <p className="mt-1 text-sm text-slate-500">{error}</p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center btn btn-primary text-sm shadow-sm"
              onClick={loadData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <PageHero
        title="Payments"
        subtitle="Payment processing, tracking, and multi-gateway management"
        actions={
          <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Process Payment
          </button>
        }
      />

      {/* Stats Overview */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="h-8 w-8 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Total Incoming</dt>
                  <dd className="text-lg font-medium text-green-600">
                    R{totalIncoming.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Total Outgoing</dt>
                  <dd className="text-lg font-medium text-red-600">
                    R{totalOutgoing.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Pending</dt>
                  <dd className="text-lg font-medium text-slate-900">{pendingPayments}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-8 w-8 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Failed</dt>
                  <dd className="text-lg font-medium text-slate-900">{failedPayments}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 max-w-lg">
          <div className="flex items-center w-full rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              name="search"
              id="search"
              className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
              placeholder="Search payments, customers, references..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter Toggle */}
          <button
            type="button"
            className={`relative inline-flex items-center px-3 py-2 text-sm font-semibold ${
              showFilters
                ? 'bg-cyan-600 text-white'
                : 'bg-white text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80'
            } rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
            {Object.keys(filters).length > 0 && (
              <span className="ml-1 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                {Object.keys(filters).length}
              </span>
            )}
          </button>

          {/* View Mode Toggle */}
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'grid'
                  ? 'bg-cyan-600 text-white border-cyan-500'
                  : 'bg-white text-slate-700 border-gray-300 hover:bg-slate-50/80'
              }`}
              onClick={() => setViewMode('grid')}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-b border-r ${
                viewMode === 'table'
                  ? 'bg-cyan-600 text-white border-cyan-500'
                  : 'bg-white text-slate-700 border-gray-300 hover:bg-slate-50/80'
              }`}
              onClick={() => setViewMode('table')}
            >
              <TableCellsIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      <Transition
        show={showFilters}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="mt-4 bg-slate-50/80 p-4 rounded-lg">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Payment Type Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Type
              </label>
              <select
                value={filters.paymentType || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, paymentType: e.target.value as PaymentType || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297387" value="">All Types</option>
                <option value={PaymentType.INCOMING}>Incoming</option>
                <option value={PaymentType.OUTGOING}>Outgoing</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as PaymentStatus || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297387" value="">All Statuses</option>
                <option value={PaymentStatus.PENDING}>Pending</option>
                <option value={PaymentStatus.PROCESSING}>Processing</option>
                <option value={PaymentStatus.COMPLETED}>Completed</option>
                <option value={PaymentStatus.FAILED}>Failed</option>
                <option value={PaymentStatus.CANCELLED}>Cancelled</option>
                <option value={PaymentStatus.REFUNDED}>Refunded</option>
              </select>
            </div>

            {/* Payment Method Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Method
              </label>
              <select
                value={filters.paymentMethod || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297387" value="">All Methods</option>
                <option value={PaymentMethod.CASH}>Cash</option>
                <option value={PaymentMethod.CREDIT_CARD}>Credit Card</option>
                <option value={PaymentMethod.BANK_TRANSFER}>Bank Transfer</option>
                <option value={PaymentMethod.CHECK}>Check</option>
                <option value={PaymentMethod.MOBILE_PAYMENT}>Mobile Payment</option>
                <option value={PaymentMethod.OTHER}>Other</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Category
              </label>
              <select
                value={filters.category || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value as PaymentCategory || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297387" value="">All Categories</option>
                <option value={PaymentCategory.SALES}>Sales</option>
                <option value={PaymentCategory.PURCHASE}>Purchase</option>
                <option value={PaymentCategory.EXPENSE}>Expense</option>
                <option value={PaymentCategory.REFUND}>Refund</option>
                <option value={PaymentCategory.SERVICE}>Service</option>
                <option value={PaymentCategory.SUBSCRIPTION}>Subscription</option>
                <option value={PaymentCategory.OTHER}>Other</option>
              </select>
            </div>
          </div>

          {/* Additional Filter Row */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
            {/* Currency Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Currency
              </label>
              <select
                value={filters.currency || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, currency: e.target.value as Currency || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297387" value="">All Currencies</option>
                <option value={Currency.ZAR}>ZAR</option>
                <option value={Currency.USD}>USD</option>
                <option value={Currency.EUR}>EUR</option>
                <option value={Currency.GBP}>GBP</option>
              </select>
            </div>

            {/* Gateway Provider Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Gateway
              </label>
              <select
                value={filters.gatewayProvider || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, gatewayProvider: e.target.value as GatewayProvider || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297387" value="">All Gateways</option>
                <option value={GatewayProvider.STRIPE}>Stripe</option>
                <option value={GatewayProvider.PAYPAL}>PayPal</option>
                <option value={GatewayProvider.PAYFAST}>PayFast</option>
                <option value={GatewayProvider.OZOW}>Ozow</option>
                <option value={GatewayProvider.MANUAL}>Manual</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Date From
              </label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  startDate: e.target.value || undefined
                }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Date To
              </label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  endDate: e.target.value || undefined
                }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              />
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="w-full inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </Transition>

      {/* Results Summary */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-slate-700">
          Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
          <span className="font-medium">
            {Math.min(currentPage * itemsPerPage, filteredPayments.length)}
          </span> of{' '}
          <span className="font-medium">{filteredPayments.length}</span> payments
        </p>

        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            <option key="paymentDate-1753461297387" value="paymentDate">Date</option>
            <option key="amount-1753461297387" value="amount">Amount</option>
            <option key="customerName-1753461297387" value="customerName">Customer</option>
            <option key="vendorName-1753461297387" value="vendorName">Vendor</option>
            <option key="status-1753461297387" value="status">Status</option>
            <option key="paymentMethod-1753461297387" value="paymentMethod">Method</option>
          </select>
          <SortOrderButton
            order={sortOrder}
            onToggle={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
          />
        </div>
      </div>

      {/* Content */}
      {viewMode === 'grid' ? (
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {paginatedPayments.map((payment) => (
            <div
              key={payment.id}
              className="col-span-1 flex flex-col divide-y divide-gray-200 rounded-lg bg-white text-center shadow"
            >
              <div className="flex flex-1 flex-col p-6">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cyan-100">
                  <CreditCardIcon className="h-8 w-8 text-cyan-600" />
                </div>
                <h3 className="mt-4 text-sm font-medium text-slate-900">
                  {payment.paymentNumber}
                </h3>
                <dl className="mt-1 flex flex-grow flex-col justify-between">
                  <dt className="sr-only">Customer/Vendor</dt>
                  <dd className="text-sm text-slate-500 font-medium">
                    {payment.customerName || payment.vendorName || 'N/A'}
                  </dd>
                  
                  <dt className="sr-only">Amount</dt>
                  <dd className="mt-3 text-lg font-semibold text-slate-900">
                    {getCurrencyDisplay(payment.currency)}{payment.amount.toLocaleString()}
                  </dd>
                  
                  <dt className="sr-only">Description</dt>
                  <dd className="mt-2 text-sm text-slate-500 line-clamp-2">
                    {payment.description}
                  </dd>
                  
                  <dt className="sr-only">Date</dt>
                  <dd className="mt-2">
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                      <CalendarIcon className="h-4 w-4" />
                      {format(new Date(payment.paymentDate), 'MMM d, yyyy')}
                    </div>
                  </dd>
                  
                  <dt className="sr-only">Payment Details</dt>
                  <dd className="mt-3 flex flex-col gap-2">
                    {getPaymentTypeBadge(payment)}
                    {getStatusBadge(payment)}
                    {getCategoryBadge(payment)}
                  </dd>
                  
                  <dt className="sr-only">Payment Method</dt>
                  <dd className="mt-2 text-xs text-slate-500">
                    {getPaymentMethodDisplay(payment.paymentMethod)}
                    {payment.gatewayProvider && payment.gatewayProvider !== GatewayProvider.MANUAL && (
                      <span> via {getGatewayProviderDisplay(payment.gatewayProvider)}</span>
                    )}
                  </dd>
                  
                  {payment.isRecurring && (
                    <dd className="mt-2 inline-flex items-center gap-1 text-xs text-cyan-600">
                      <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden />
                      Recurring {payment.recurringFrequency}
                    </dd>
                  )}
                </dl>
              </div>
              <div>
                <div className="-mt-px flex divide-x divide-gray-200">
                  <div className="flex w-0 flex-1">
                    <button
                      onClick={() => openViewModal(payment)}
                      className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-slate-900 hover:bg-slate-50/80"
                    >
                      <EyeIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                      View
                    </button>
                  </div>
                  <div className="-ml-px flex w-0 flex-1">
                    <button
                      onClick={() => openEditModal(payment)}
                      className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 border border-transparent py-4 text-sm font-semibold text-slate-900 hover:bg-slate-50/80"
                    >
                      <PencilIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                      Edit
                    </button>
                  </div>
                  <div className="-ml-px flex w-0 flex-1">
                    <button
                      onClick={() => openDeleteModal(payment)}
                      className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-red-600 hover:bg-slate-50/80"
                    >
                      <TrashIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6">
                        Payment
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Type
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Customer/Vendor
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Amount
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Method
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Date
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedPayments.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-4">
                          <EmptyState
                            className="mt-0"
                            title="No payments found"
                            description="Get started by processing your first payment."
                            icon={<CreditCardIcon className="h-6 w-6" />}
                            action={
                              <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary text-sm focus-pop">
                                <PlusIcon className="h-5 w-5" />
                                Process Payment
                              </button>
                            }
                          />
                        </td>
                      </tr>
                    ) : (
                      paginatedPayments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-slate-50/80">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
                                  <CreditCardIcon className="h-6 w-6 text-cyan-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="font-medium text-slate-900">
                                  {payment.paymentNumber}
                                </div>
                                <div className="text-slate-500 text-xs truncate max-w-48">
                                  {payment.description}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            {getPaymentTypeBadge(payment)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-900">
                            <div>
                              <div className="font-medium">
                                {payment.customerName || payment.vendorName || 'N/A'}
                              </div>
                              <div className="text-slate-500 text-xs">
                                {getCategoryBadge(payment)}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-slate-900">
                            {getCurrencyDisplay(payment.currency)}{payment.amount.toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            {getStatusBadge(payment)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            <div className="text-sm text-slate-900">
                              {getPaymentMethodDisplay(payment.paymentMethod)}
                            </div>
                            {payment.gatewayProvider && payment.gatewayProvider !== GatewayProvider.MANUAL && (
                              <div className="text-xs text-slate-500">
                                via {getGatewayProviderDisplay(payment.gatewayProvider)}
                              </div>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            <div>{format(new Date(payment.paymentDate), 'MMM d, yyyy')}</div>
                            {payment.isRecurring && (
                              <div className="inline-flex items-center gap-1 text-xs text-cyan-600">
                                <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden />
                                Recurring
                              </div>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openViewModal(payment)}
                                className="text-cyan-600 hover:text-indigo-900"
                                title="View"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openEditModal(payment)}
                                className="text-slate-600 hover:text-slate-900"
                                title="Edit"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openDeleteModal(payment)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pageNum === currentPage
                          ? 'z-10 bg-cyan-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600'
                          : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      <BrandModal open={showAddModal} onClose={() => setShowAddModal(false)} maxWidthClass="max-w-4xl">
        <BrandModalHeader title="Create New Payment" onClose={() => setShowAddModal(false)} />
        <BrandModalBody>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Payment Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Payment Type *
                    </label>
                    <select
                      value={formData.paymentType}
                      onChange={(e) => handleFormChange('paymentType', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      <option value={PaymentType.INCOMING}>Incoming</option>
                      <option value={PaymentType.OUTGOING}>Outgoing</option>
                    </select>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Payment Method *
                    </label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(PaymentMethod).map(method => (
                        <option key={method} value={method}>
                          {getPaymentMethodDisplay(method)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Amount *
                    </label>
                    <AmountInput
                      value={formData.amount}
                      onChange={(v) => handleFormChange('amount', v ?? 0)}
                      min={0}
                      prefix="R"
                      inputClassName={`block w-full rounded-md border-0 py-1.5 pl-9 text-slate-900 shadow-sm ring-1 ring-inset ${
                        formErrors.amount ? 'ring-red-300' : 'ring-gray-300'
                      } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                    />
                    {formErrors.amount && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>
                    )}
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Currency
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => handleFormChange('currency', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(Currency).map(currency => (
                        <option key={currency} value={currency}>
                          {getCurrencyDisplay(currency)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleFormChange('category', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(PaymentCategory).map(category => (
                        <option key={category} value={category}>
                          {getPaymentCategoryDisplay(category)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Payment Date */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => handleFormChange('paymentDate', e.target.value)}
                      className={`block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ${
                        formErrors.paymentDate ? 'ring-red-300' : 'ring-gray-300'
                      } focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                    />
                    {formErrors.paymentDate && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.paymentDate}</p>
                    )}
                  </div>

                  {/* Customer Name (for incoming payments) */}
                  {formData.paymentType === PaymentType.INCOMING && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Customer Name *
                        </label>
                        <input
                          type="text"
                          value={formData.customerName || ''}
                          onChange={(e) => handleFormChange('customerName', e.target.value)}
                          className={`block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ${
                            formErrors.customerName ? 'ring-red-300' : 'ring-gray-300'
                          } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                          placeholder="Enter customer name"
                        />
                        {formErrors.customerName && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.customerName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Customer Email
                        </label>
                        <input
                          type="email"
                          value={formData.customerEmail || ''}
                          onChange={(e) => handleFormChange('customerEmail', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                          placeholder="customer@example.com"
                        />
                      </div>
                    </>
                  )}

                  {/* Vendor Name (for outgoing payments) */}
                  {formData.paymentType === PaymentType.OUTGOING && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Vendor Name *
                        </label>
                        <input
                          type="text"
                          value={formData.vendorName || ''}
                          onChange={(e) => handleFormChange('vendorName', e.target.value)}
                          className={`block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ${
                            formErrors.vendorName ? 'ring-red-300' : 'ring-gray-300'
                          } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                          placeholder="Enter vendor name"
                        />
                        {formErrors.vendorName && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.vendorName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Vendor Email
                        </label>
                        <input
                          type="email"
                          value={formData.vendorEmail || ''}
                          onChange={(e) => handleFormChange('vendorEmail', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                          placeholder="vendor@example.com"
                        />
                      </div>
                    </>
                  )}

                  {/* Reference */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Reference
                    </label>
                    <input
                      type="text"
                      value={formData.reference || ''}
                      onChange={(e) => handleFormChange('reference', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                      placeholder="Payment reference"
                    />
                  </div>

                  {/* Invoice Number */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      value={formData.invoiceNumber || ''}
                      onChange={(e) => handleFormChange('invoiceNumber', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                      placeholder="INV-001"
                    />
                  </div>

                  {/* Gateway Provider */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Gateway Provider
                    </label>
                    <select
                      value={formData.gatewayProvider || GatewayProvider.MANUAL}
                      onChange={(e) => handleFormChange('gatewayProvider', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(GatewayProvider).map(provider => (
                        <option key={provider} value={provider}>
                          {getGatewayProviderDisplay(provider)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Transaction ID */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Transaction ID
                    </label>
                    <input
                      type="text"
                      value={formData.transactionId || ''}
                      onChange={(e) => handleFormChange('transactionId', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                      placeholder="TXN123456"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    className={`block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ${
                      formErrors.description ? 'ring-red-300' : 'ring-gray-300'
                    } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                    placeholder="Enter payment description..."
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                  )}
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={2}
                    value={formData.notes || ''}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    placeholder="Additional notes..."
                  />
                </div>

                {/* Recurring Payment */}
                <div className="mt-4">
                  <div className="flex items-center">
                    <input
                      id="editIsRecurring"
                      type="checkbox"
                      checked={formData.isRecurring}
                      onChange={(e) => handleFormChange('isRecurring', e.target.checked)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editIsRecurring" className="ml-2 block text-sm text-slate-900">
                      This is a recurring payment
                    </label>
                  </div>
                  
                  {formData.isRecurring && (
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Frequency
                        </label>
                        <select
                          value={formData.recurringFrequency || RecurringFrequency.MONTHLY}
                          onChange={(e) => handleFormChange('recurringFrequency', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                        >
                          {Object.values(RecurringFrequency).map(freq => (
                            <option key={freq} value={freq}>
                              {freq.charAt(0) + freq.slice(1).toLowerCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Next Due Date
                        </label>
                        <input
                          type="date"
                          value={formData.nextDueDate || ''}
                          onChange={(e) => handleFormChange('nextDueDate', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                  )}
                </div>
        </BrandModalBody>
        <BrandModalFooter>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  disabled={isSubmitting}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreatePayment}
                  disabled={isSubmitting}
                  className="btn btn-primary text-sm"
                >
                  {isSubmitting ? 'Creating...' : 'Create Payment'}
                </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Edit Payment Modal */}
      <BrandModal
        open={showEditModal && !!selectedPayment}
        onClose={() => setShowEditModal(false)}
        maxWidthClass="max-w-4xl"
      >
        <BrandModalHeader
          title={selectedPayment ? `Edit Payment - ${selectedPayment.paymentNumber}` : 'Edit Payment'}
          onClose={() => setShowEditModal(false)}
        />
        <BrandModalBody>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Payment Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Payment Type *
                    </label>
                    <select
                      value={formData.paymentType}
                      onChange={(e) => handleFormChange('paymentType', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      <option value={PaymentType.INCOMING}>Incoming</option>
                      <option value={PaymentType.OUTGOING}>Outgoing</option>
                    </select>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Payment Method *
                    </label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(PaymentMethod).map(method => (
                        <option key={method} value={method}>
                          {getPaymentMethodDisplay(method)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Amount *
                    </label>
                    <AmountInput
                      value={formData.amount}
                      onChange={(v) => handleFormChange('amount', v ?? 0)}
                      min={0}
                      prefix="R"
                      inputClassName={`block w-full rounded-md border-0 py-1.5 pl-9 text-slate-900 shadow-sm ring-1 ring-inset ${
                        formErrors.amount ? 'ring-red-300' : 'ring-gray-300'
                      } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                    />
                    {formErrors.amount && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>
                    )}
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Currency
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => handleFormChange('currency', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(Currency).map(currency => (
                        <option key={currency} value={currency}>
                          {getCurrencyDisplay(currency)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(PaymentStatus).map(status => (
                        <option key={status} value={status}>
                          {getPaymentStatusDisplay(status)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleFormChange('category', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(PaymentCategory).map(category => (
                        <option key={category} value={category}>
                          {getPaymentCategoryDisplay(category)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Customer Name (for incoming payments) */}
                  {formData.paymentType === PaymentType.INCOMING && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Customer Name *
                        </label>
                        <input
                          type="text"
                          value={formData.customerName || ''}
                          onChange={(e) => handleFormChange('customerName', e.target.value)}
                          className={`block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ${
                            formErrors.customerName ? 'ring-red-300' : 'ring-gray-300'
                          } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                          placeholder="Enter customer name"
                        />
                        {formErrors.customerName && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.customerName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Customer Email
                        </label>
                        <input
                          type="email"
                          value={formData.customerEmail || ''}
                          onChange={(e) => handleFormChange('customerEmail', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                          placeholder="customer@example.com"
                        />
                      </div>
                    </>
                  )}

                  {/* Vendor Name (for outgoing payments) */}
                  {formData.paymentType === PaymentType.OUTGOING && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Vendor Name *
                        </label>
                        <input
                          type="text"
                          value={formData.vendorName || ''}
                          onChange={(e) => handleFormChange('vendorName', e.target.value)}
                          className={`block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ${
                            formErrors.vendorName ? 'ring-red-300' : 'ring-gray-300'
                          } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                          placeholder="Enter vendor name"
                        />
                        {formErrors.vendorName && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.vendorName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Vendor Email
                        </label>
                        <input
                          type="email"
                          value={formData.vendorEmail || ''}
                          onChange={(e) => handleFormChange('vendorEmail', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                          placeholder="vendor@example.com"
                        />
                      </div>
                    </>
                  )}

                  {/* Reference */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Reference
                    </label>
                    <input
                      type="text"
                      value={formData.reference || ''}
                      onChange={(e) => handleFormChange('reference', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                      placeholder="Payment reference"
                    />
                  </div>

                  {/* Invoice Number */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      value={formData.invoiceNumber || ''}
                      onChange={(e) => handleFormChange('invoiceNumber', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                      placeholder="INV-001"
                    />
                  </div>

                  {/* Gateway Provider */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Gateway Provider
                    </label>
                    <select
                      value={formData.gatewayProvider || GatewayProvider.MANUAL}
                      onChange={(e) => handleFormChange('gatewayProvider', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    >
                      {Object.values(GatewayProvider).map(provider => (
                        <option key={provider} value={provider}>
                          {getGatewayProviderDisplay(provider)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Transaction ID */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Transaction ID
                    </label>
                    <input
                      type="text"
                      value={formData.transactionId || ''}
                      onChange={(e) => handleFormChange('transactionId', e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                      placeholder="TXN123456"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    className={`block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ${
                      formErrors.description ? 'ring-red-300' : 'ring-gray-300'
                    } placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6`}
                    placeholder="Enter payment description..."
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                  )}
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={2}
                    value={formData.notes || ''}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                    placeholder="Additional notes..."
                  />
                </div>

                {/* Recurring Payment */}
                <div className="mt-4">
                  <div className="flex items-center">
                    <input
                      id="editIsRecurring"
                      type="checkbox"
                      checked={formData.isRecurring}
                      onChange={(e) => handleFormChange('isRecurring', e.target.checked)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editIsRecurring" className="ml-2 block text-sm text-slate-900">
                      This is a recurring payment
                    </label>
                  </div>
                  
                  {formData.isRecurring && (
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Frequency
                        </label>
                        <select
                          value={formData.recurringFrequency || RecurringFrequency.MONTHLY}
                          onChange={(e) => handleFormChange('recurringFrequency', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                        >
                          {Object.values(RecurringFrequency).map(freq => (
                            <option key={freq} value={freq}>
                              {freq.charAt(0) + freq.slice(1).toLowerCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Next Due Date
                        </label>
                        <input
                          type="date"
                          value={formData.nextDueDate || ''}
                          onChange={(e) => handleFormChange('nextDueDate', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                  )}
                </div>
        </BrandModalBody>
        <BrandModalFooter>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmitting}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdatePayment}
                  disabled={isSubmitting}
                  className="btn btn-primary text-sm"
                >
                  {isSubmitting ? 'Updating...' : 'Update Payment'}
                </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Delete Confirmation Modal */}
      <BrandModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidthClass="max-w-lg">
        <BrandModalHeader title="Delete Payment" onClose={() => setShowDeleteModal(false)} />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete this payment? This action cannot be undone and will permanently remove the payment record.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger text-sm"
            onClick={handleDeletePayment}
          >
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>

      {/* View Modal */}
      <BrandModal
        open={showViewModal && !!selectedPayment}
        onClose={() => setShowViewModal(false)}
        maxWidthClass="max-w-4xl"
      >
        {selectedPayment ? (
          <>
            <BrandModalHeader
              title={`Payment Details - ${selectedPayment.paymentNumber}`}
              onClose={() => setShowViewModal(false)}
            />
            <BrandModalBody>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <h4 className="font-medium text-slate-900">Payment Information</h4>
                    <p className="text-sm text-slate-600">Type: {getPaymentTypeDisplay(selectedPayment.paymentType)}</p>
                    <p className="text-sm text-slate-600">Amount: {getCurrencyDisplay(selectedPayment.currency)} {selectedPayment.amount.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Method: {getPaymentMethodDisplay(selectedPayment.paymentMethod)}</p>
                    <p className="text-sm text-slate-600">Date: {format(new Date(selectedPayment.paymentDate), 'PPP')}</p>
                    <div className="flex gap-2 mt-2">
                      {getPaymentTypeBadge(selectedPayment)}
                      {getStatusBadge(selectedPayment)}
                      {getCategoryBadge(selectedPayment)}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">Party Information</h4>
                    {selectedPayment.customerName && (
                      <>
                        <p className="text-sm text-slate-600">Customer: {selectedPayment.customerName}</p>
                        <p className="text-sm text-slate-600">Email: {selectedPayment.customerEmail}</p>
                      </>
                    )}
                    {selectedPayment.vendorName && (
                      <>
                        <p className="text-sm text-slate-600">Vendor: {selectedPayment.vendorName}</p>
                        <p className="text-sm text-slate-600">Email: {selectedPayment.vendorEmail}</p>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="mt-6">
                  <h4 className="font-medium text-slate-900">Description</h4>
                  <p className="text-sm text-slate-600 mt-1">{selectedPayment.description}</p>
                </div>
                
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <h4 className="font-medium text-slate-900">Transaction Details</h4>
                    {selectedPayment.transactionId && (
                      <p className="text-sm text-slate-600">Transaction ID: {selectedPayment.transactionId}</p>
                    )}
                    {selectedPayment.reference && (
                      <p className="text-sm text-slate-600">Reference: {selectedPayment.reference}</p>
                    )}
                    {selectedPayment.invoiceNumber && (
                      <p className="text-sm text-slate-600">Invoice: {selectedPayment.invoiceNumber}</p>
                    )}
                    {selectedPayment.gatewayProvider && (
                      <p className="text-sm text-slate-600">Gateway: {getGatewayProviderDisplay(selectedPayment.gatewayProvider)}</p>
                    )}
                    {selectedPayment.fees && selectedPayment.fees > 0 && (
                      <p className="text-sm text-slate-600">Fees: {getCurrencyDisplay(selectedPayment.currency)} {selectedPayment.fees.toLocaleString()}</p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">Additional Information</h4>
                    {selectedPayment.processedDate && (
                      <p className="text-sm text-slate-600">Processed: {format(new Date(selectedPayment.processedDate), 'PPP')}</p>
                    )}
                    {selectedPayment.isRecurring && (
                      <>
                        <p className="text-sm text-slate-600">Recurring: {selectedPayment.recurringFrequency}</p>
                        {selectedPayment.nextDueDate && (
                          <p className="text-sm text-slate-600">Next Due: {format(new Date(selectedPayment.nextDueDate), 'PPP')}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {selectedPayment.notes && (
                  <div className="mt-4">
                    <h4 className="font-medium text-slate-900">Notes</h4>
                    <p className="text-sm text-slate-600 mt-1">{selectedPayment.notes}</p>
                  </div>
                )}
            </BrandModalBody>
            <BrandModalFooter>
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="btn btn-primary text-sm"
              >
                Close
              </button>
            </BrandModalFooter>
          </>
        ) : null}
      </BrandModal>
    </div>
  );
} 